import type { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import type { StandardToolResult } from '../../../../types/standardToolResults';
import { createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('TRANSCRIPT_COMMANDS');

export const transcriptCreateCommand: SelfContainedCommand = {
  id: 'transcript_create',
  description: '🎙️ CREATE: Create a new Transcript — a voice recording with speaker segments and entity extraction',
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Title for the transcript (default: "Untitled Transcript")',
        required: false,
        defaultValue: 'Untitled Transcript',
      },
      {
        name: 'recordedAt',
        type: 'string',
        description: 'ISO 8601 recording timestamp',
        required: false,
      },
      {
        name: 'source',
        type: 'string',
        description: 'Source: plaud | manual | demo',
        required: false,
        defaultValue: 'manual',
      },
    ],
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { name = 'Untitled Transcript', recordedAt, source = 'manual' } = params;
    const spaceId = context.spaceId;

    try {
      log.info('[transcript_create] Creating transcript:', { name, spaceId });

      const entityStoreModule = await import('../../../../stores/entityStore');
      const entityStore = entityStoreModule.default.getState();

      const newTranscript = await entityStore.actions.createEntity({
        entityType: 'Transcript',
        name: name.trim() || 'Untitled Transcript',
        spaceId,
        skeleton: {
          '@type': 'Transcript',
          source,
          audioUrl: null,
          audioDurationMs: 0,
          recordedAt: recordedAt ?? new Date().toISOString(),
          speakers: [],
          segments: [],
          summary: null,
          markdown: null,
          extractionRunIds: [],
          extractedEntityRollup: {},
          base64: '',
          version: '1.0',
          architecture: 'base-application',
          editor: 'blocknote',
          groupIds: [],
        } as any,
      });

      const entityId = newTranscript._id || newTranscript.id;
      log.info('[transcript_create] Transcript created:', entityId);

      return createSuccessResult(`Transcript created: ${name}`, {
        entityId,
        name: newTranscript.name,
        entityType: 'Transcript',
      });
    } catch (error) {
      log.error('[transcript_create] Failed:', error);
      return createErrorResult(
        'Failed to create transcript',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
      );
    }
  },
};

entityCreationRegistry.register({
  commandId: 'transcript_create',
  entityType: 'Transcript',
  displayName: 'Transcript',
  icon: { type: 'phosphor', phosphorIcon: 'Microphone', phosphorWeight: 'duotone' },
  description: 'Voice recording with entity extraction',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'document',
  sortOrder: 25,
});
